/**
 * Convert API chat history items to ChatMessage[] for display.
 * API returns newest first; we pass items in display order (oldest first) after reversing.
 */
import type {
  ChatMessage,
  PhoneLinkDto,
  LinkDto,
  BookingDataDto,
} from "../types/chat";

function parsePhoneLinks(links: unknown): PhoneLinkDto[] | undefined {
  if (!links) return undefined;
  if (Array.isArray(links)) return links as PhoneLinkDto[];
  if (typeof links === "string") {
    try {
      const parsed = JSON.parse(links);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function parseUrlLinks(links: unknown): LinkDto[] | undefined {
  if (!links) return undefined;
  if (Array.isArray(links)) return links as LinkDto[];
  if (typeof links === "string") {
    try {
      const parsed = JSON.parse(links);
      return Array.isArray(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function parseBookingData(raw: unknown): BookingDataDto | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "string") {
    try {
      return parseBookingData(JSON.parse(raw));
    } catch {
      return undefined;
    }
  }
  if (typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const appointmentId = Number(o.appointmentId ?? o.appointment_id);
  if (!Number.isFinite(appointmentId)) return undefined;
  return {
    appointmentId,
    doctorName: String(o.doctorName ?? o.doctor_name ?? ""),
    date: String(o.date ?? ""),
    time: String(o.time ?? ""),
  };
}

/** e.g. medpharma://appointments/42 → "42" */
export function parseAppointmentIdFromActionLink(url: string): string | null {
  const trimmed = url.trim();
  const med = trimmed.match(/^medpharma:\/\/appointments\/(\d+)/i);
  if (med) return med[1];
  if (/^https?:\/\//i.test(trimmed)) {
    const web = trimmed.match(/\/appointment\/(\d+)(?:\/?|\?|#|$)/i);
    if (web) return web[1];
    const main = trimmed.match(/\/main\/appointments\/(\d+)(?:\/?|\?|#|$)/i);
    if (main) return main[1];
  }
  const rel = trimmed.match(/^\/?main\/appointments\/(\d+)(?:\/?|$)/i);
  if (rel) return rel[1];
  const relAppt = trimmed.match(/^\/?appointment\/(\d+)(?:\/?|$)/i);
  if (relAppt) return relAppt[1];
  return null;
}

/** When history only has reply text (no booking metadata from API). */
export function isAppointmentConfirmationMessage(content: string): boolean {
  const t = content.toLowerCase();
  if (!t.trim()) return false;
  if (/appointment is confirmed/.test(t)) return true;
  if (/telehealth appointment is confirmed/.test(t)) return true;
  if (/in-person appointment is confirmed/.test(t)) return true;
  return (
    /\bconfirmed\b/.test(t) &&
    /doctor\s*:/.test(t) &&
    /date\s*:/.test(t) &&
    /time\s*:/.test(t)
  );
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

function ordinalSuffix(day: number): string {
  const k = day % 100;
  if (k >= 11 && k <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

function parseBookingDateParts(
  raw: string,
): { day: number; month: number; year: number } | null {
  const s = raw.trim();
  const dmy = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
  if (dmy) {
    const day = parseInt(dmy[1], 10);
    const month = parseInt(dmy[2], 10) - 1;
    const year = parseInt(dmy[3], 10);
    return validateCalendarDate(year, month, day);
  }
  const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (ymd) {
    const year = parseInt(ymd[1], 10);
    const month = parseInt(ymd[2], 10) - 1;
    const day = parseInt(ymd[3], 10);
    return validateCalendarDate(year, month, day);
  }
  return null;
}

function validateCalendarDate(
  year: number,
  month: number,
  day: number,
): { day: number; month: number; year: number } | null {
  const dt = new Date(year, month, day);
  if (
    dt.getFullYear() !== year ||
    dt.getMonth() !== month ||
    dt.getDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/**
 * Booking API dates are often DD-MM-YYYY (e.g. 01-04-2026). Human-readable:
 * "1st of April, 2026".
 */
export function formatBookingDateForDisplay(raw: string): string {
  const s = raw.trim();
  const parts = parseBookingDateParts(s);
  if (!parts) return s;
  const { day, year } = parts;
  const monthName = MONTH_NAMES[parts.month];
  return `${day}${ordinalSuffix(day)} of ${monthName}, ${year}`;
}

const APPOINTMENT_DATE_AFTER_LABEL =
  /(?:\*\*Date:\*\*\s*|Date\s*:\s*)(\d{1,2}[-/]\d{1,2}[-/]\d{4}|\d{4}-\d{1,2}-\d{1,2}(?:T[\d:.Z+-]+)?)/gi;

export function humanizeAppointmentDatesInContent(content: string): string {
  if (!isAppointmentConfirmationMessage(content)) return content;
  return content.replace(
    APPOINTMENT_DATE_AFTER_LABEL,
    (match, dateToken: string) =>
      match.replace(dateToken, formatBookingDateForDisplay(dateToken)),
  );
}

const AVAILABLE_SLOTS_LISTING_MARKERS =
  /available appointments?:|closest available appointment slots|here are the closest available/i;

function isAvailableAppointmentsListingMessage(content: string): boolean {
  if (!content.trim()) return false;
  return AVAILABLE_SLOTS_LISTING_MARKERS.test(content);
}

const LISTING_INLINE_BOOKING_DATES =
  /\b(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{4})\b/g;

export function humanizeAvailableSlotsDatesInContent(content: string): string {
  if (!isAvailableAppointmentsListingMessage(content)) return content;
  return content.replace(LISTING_INLINE_BOOKING_DATES, (match) =>
    formatBookingDateForDisplay(match),
  );
}

export function humanizeAssistantBookingDatesInContent(
  content: string,
): string {
  return humanizeAvailableSlotsDatesInContent(
    humanizeAppointmentDatesInContent(content),
  );
}

export function shouldHumanizeAssistantBookingDates(content: string): boolean {
  if (!content.trim()) return false;
  if (AVAILABLE_SLOTS_LISTING_MARKERS.test(content)) return true;
  return isAppointmentConfirmationMessage(content);
}

export function chatHistoryItemsToMessages(
  items: Array<{
    id?: number;
    message?: string;
    reply?: string;
    imageUrl?: string | null;
    imageSignedUrl?: string | null;
    createdAt?: string;
    created_at?: string;
    phoneLinks?: unknown;
    phone_links?: unknown;
    links?: unknown;
    url_links?: unknown;
    actionLink?: string;
    action_link?: string;
    bookingData?: unknown;
    image_url?: string | null;
    image_signed_url?: string | null;
  }>,
): ChatMessage[] {
  return items.flatMap((item) => {
    const phoneLinks = parsePhoneLinks(item.phoneLinks ?? item.phone_links);
    const links = parseUrlLinks(item.links ?? item.url_links);
    const actionLink = item.actionLink ?? item.action_link;
    const bookingData = parseBookingData(item.bookingData);
    const imageUrl =
      item.imageSignedUrl ??
      item.image_signed_url ??
      item.imageUrl ??
      item.image_url;
    const itemTimestamp = item.createdAt ?? item.created_at;
    const idPrefix = item.id != null ? String(item.id) : undefined;

    if (imageUrl) {
      const userMsg: ChatMessage = {
        ...(idPrefix && { id: `${idPrefix}-u` }),
        role: "user",
        content: imageUrl,
        type: "image",
        ...(itemTimestamp && { timestamp: String(itemTimestamp) }),
      };
      if (item.reply?.trim()) {
        return [
          userMsg,
          {
            ...(idPrefix && { id: `${idPrefix}-r` }),
            role: "assistant",
            content: item.reply,
            phoneLinks,
            links,
            actionLink,
            bookingData,
            ...(itemTimestamp && { timestamp: String(itemTimestamp) }),
          },
        ];
      }
      return [userMsg];
    }

    const hasMessage = item.message?.trim();
    const hasReply = item.reply?.trim();
    if (!hasMessage && !hasReply) return [];

    const messages: ChatMessage[] = [];
    const timestampOpt =
      itemTimestamp != null ? { timestamp: String(itemTimestamp) } : {};
    if (hasMessage) {
      messages.push({
        ...(idPrefix && { id: `${idPrefix}-u` }),
        role: "user",
        content: item.message!.trim(),
        ...timestampOpt,
      });
    }
    if (hasReply) {
      messages.push({
        ...(idPrefix && { id: `${idPrefix}-r` }),
        role: "assistant",
        content: item.reply!.trim(),
        phoneLinks,
        links,
        actionLink,
        bookingData,
        ...timestampOpt,
      });
    }
    return messages;
  });
}

export function getDateKey(isoString: string | undefined): string | null {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

export function formatDateSeparator(isoString: string): string {
  const d = new Date(isoString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const key = d.toISOString().slice(0, 10);
  const todayKey = today.toISOString().slice(0, 10);
  const yesterdayKey = yesterday.toISOString().slice(0, 10);
  if (key === todayKey) return "Today";
  if (key === yesterdayKey) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatMessageTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
